import { useState, useEffect, useRef } from "react";

interface Props {
  selectedValue: string | number | undefined;
  setSelectedValue: (value: string | number) => boolean | void;
  options: (string | number)[];
  type: 'month' | 'year' | 'newPatientMonth' | 'newPatientYear';
}

export const MonthYearDropdown = ({ selectedValue, setSelectedValue, options, type = 'month' }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  // Handle wheel/touchpad scrolling
  useEffect(() => {
    if (!isOpen || !listRef.current) return;

    const handleWheel = (e: WheelEvent) => {
      if (listRef.current) {
        // Prevent page scroll when scrolling the dropdown
        if (e.deltaY !== 0) {
          e.preventDefault();
          listRef.current.scrollTop += e.deltaY;
        }
      }
    };

    const listElement = listRef.current;
    listElement?.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      listElement?.removeEventListener('wheel', handleWheel);
    };
  }, [isOpen]);

  // Auto-scroll to selected value when opening
  useEffect(() => {
    if (isOpen && listRef.current) {
      const selectedIndex = options.indexOf(selectedValue as string | number);
      if (selectedIndex >= 0) {
        const itemHeight = 24; // Approximate height of each item
        listRef.current.scrollTop = selectedIndex * itemHeight - itemHeight * 2;
      }
    }
  }, [isOpen, selectedValue, options]);

  // Decide dropdown position on open
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen && dropdownRef.current) {
      const triggerRect = dropdownRef.current.getBoundingClientRect();
      const dropdownHeight = 168;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
    setIsOpen(!isOpen);
  };

  const handleValueSelect = (value: string | number) => {
    const result = setSelectedValue(value);
    // Only close dropdown if validation passes (result is not false)
    if (result !== false) {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Custom select trigger */}
      <button
        className={`
          flex items-center justify-center gap-1 border border-transparent hover:border-[#A2A9B0] shadow-none focus:outline-none focus:ring-2 focus:ring-blue-200 text-[#4D5358] text-xs leading-normal
          ${type === 'newPatientMonth' || type === 'newPatientYear' ? 'bg-white h-[1.1875rem] px-2 py-0.5 rounded font-normal' : 'bg-transparent h-[0.9375rem] p-0 px-0.5 rounded-sm font-semibold'}
        `}
        onClick={handleButtonClick}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="overflow-hidden whitespace-nowrap text-ellipsis">
          {selectedValue ? selectedValue : type === 'newPatientMonth' ? 'Select start month' : type === 'newPatientYear' ? 'Select start year' : 'Select month'}
        </span>
        <div className="flex-shrink-0 mt-0.5">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="7" 
            height="5" 
            viewBox="0 0 7 5" 
            fill="none"
          >
            <path 
              d="M1.40039 1.4502L3.50039 3.5502L5.60039 1.4502" 
              stroke="#878D96" 
              strokeWidth="1.4" 
              strokeLinecap="round"
            />
          </svg>
        </div>
      </button>

      {/* Custom dropdown content */}
      {isOpen && (
        <div
          className={`absolute left-1/2 z-50 bg-white h-[10.5rem] p-1 mt-0.5 rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)] border-none overflow-hidden
            ${type === 'newPatientMonth' ? 'min-w-[6.375rem]' : type === 'newPatientYear' ? 'min-w-[3.5rem] h-[9.15rem]' : type === 'month' ? 'min-w-[5.1875rem]' : 'min-w-[3.1875rem]'}
          `}
          style={dropdownPosition === 'top'
            ? { bottom: '100%', left: '50%', transform: 'translateX(-50%)' }
            : { top: '100%', left: '50%', transform: 'translateX(-50%)' }
          }
          role="listbox"
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            ref={listRef}
            className="flex flex-col h-full overflow-y-auto [&::-webkit-scrollbar]:w-[0.125rem] [&::-webkit-scrollbar]:rounded-sm [&::-webkit-scrollbar-thumb]:bg-[#DDE1E6] [&::-webkit-scrollbar-track]:bg-transparent"
          >
            {options.map((option) => (
              <button
                key={option}
                className={`w-full text-left px-2 py-1 text-[#697077] hover:text-[#4D5358] text-[0.625rem] font-normal leading-normal cursor-pointer transition-colors duration-100
                  ${selectedValue === option ? 'bg-[#F2F4F8]' : 'hover:bg-[#FAFAFA]'}
                `}
                onClick={(e) => {
                  e.stopPropagation();
                  handleValueSelect(option);
                }}
                role="option"
                aria-selected={selectedValue === option}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
